import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaSave, 
  FaSpinner, 
  FaBox, 
  FaClipboard, 
  FaRuler 
} from 'react-icons/fa';
import { getProductById, createProduct, updateProduct } from './productService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import {
  FormContainer,
  FormSection,
  FormHeader,
  BackButton,
  FormGrid,
  FormField,
  InputWrapper,
  SubmitButton
} from '../../components/styled/FormComponents';
import styled from 'styled-components';

const SelectWrapper = styled(InputWrapper)`
  select {
    width: 100%;
    padding: ${props => props.theme.spacing(2)};
    padding-left: ${props => props.theme.spacing(6)};
    background: none;
    border: none;
    color: ${props => props.theme.colors.textPrimary};
    font-size: ${props => props.theme.typography.fontSize.medium};
    appearance: none;
    cursor: pointer;

    &:focus {
      outline: none;
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  select>option{
  background-color: ${props => props.theme.colors.inputGray};;
  }

  &::after {
    content: '';
    position: absolute;
    right: ${props => props.theme.spacing(2)};
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${props => props.theme.colors.textPrimary};
    pointer-events: none;
    background: #000;
  }
`;

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unit: 'Yard'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const units = [
    { value: 'Yard', label: 'Yard' },
    { value: 'Ton', label: 'Ton' },
    { value: 'SqFt', label: 'Square Feet' }
  ];

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const response = await getProductById(id);
        setFormData({
          name: response.data.name,
          description: response.data.description || '',
          unit: response.data.unit
        });
      } catch (err) {
        setError('Failed to load product');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (id) {
        await updateProduct(id, formData);
      } else {
        await createProduct(formData);
      }
      navigate('/suppliers/products');
    } catch (err) {
      setError('Failed to save product');
      console.error('Error saving product:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/suppliers/products')}>
            <FaArrowLeft /> Back to Products
          </BackButton>
          <h2>{id ? 'Edit Product' : 'New Product'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>Product Information</h3>
            <FormGrid>
              <FormField index={0}>
                <Input 
                  label="Product Name"
                  icon={<FaBox />}
                  value={formData.name}
                  onChange={(e) => setFormData({
                    ...formData,
                    name: e.target.value
                  })}
                  placeholder="Enter product name"
                  required
                  disabled={loading}
                />
              </FormField>

              <FormField index={1}>
                <Input 
                  label="Description"
                  icon={<FaClipboard />}
                  value={formData.description}
                  onChange={(e) => setFormData({
                    ...formData,
                    description: e.target.value
                  })}
                  placeholder="Enter product description"
                  disabled={loading}
                />
              </FormField>

              <FormField index={2}>
                <label>Measurement Unit</label>
                <SelectWrapper>
                  <FaRuler />
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({
                      ...formData,
                      unit: e.target.value
                    })}
                    disabled={loading}
                    required
                  >
                    {units.map(unit => (
                      <option key={unit.value} value={unit.value}>
                        {unit.label}
                      </option>
                    ))}
                  </select>
                </SelectWrapper>
              </FormField>
            </FormGrid>

            {error && (
              <FormField index={3}>
                <div className="error-message">{error}</div>
              </FormField>
            )}
          </FormSection>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <FaSpinner className="icon-spin" />
                {id ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <FaSave />
                {id ? 'Update Product' : 'Create Product'}
              </>
            )}
          </SubmitButton>
        </form>
      </FormContainer>
    </DashboardLayout>
  );
};

export default ProductForm;